import { redirect } from 'react-router-dom';

import { myIdentificationQueryDefinition } from '@/entities/identification';
import { sessionStore } from '@/entities/session';
import { queryClient } from '@/app/providers/queryClient';

import { appendReturnTo } from './returnTo';

/** Wait for `sessionStore` to leave the initial `loading` state. */
export async function waitForSessionReady(): Promise<void> {
  const initial = sessionStore.get();
  if (initial.status !== 'loading') {
    return;
  }

  await new Promise<void>((resolve) => {
    const unsub = sessionStore.subscribe((next) => {
      if (next.status === 'loading') {
        return;
      }
      unsub();
      resolve();
    });
  });
}

/**
 * Require an authenticated session; otherwise redirect to `/login`.
 * When `returnTo` is provided the original path is forwarded as a query
 * parameter so the user lands on it after completing login + onboarding.
 */
export async function requireAuth(returnTo?: string): Promise<void> {
  await waitForSessionReady();

  const loginUrl = appendReturnTo('/login', returnTo);

  const session = sessionStore.get();
  switch (session.status) {
    case 'authenticated':
      void queryClient.prefetchQuery(myIdentificationQueryDefinition());
      return;
    case 'unauthenticated':
      throw redirect(loginUrl);
    case 'loading':
      throw redirect(loginUrl);
    default: {
      const _exhaustive: never = session.status;
      void _exhaustive;
      throw redirect(loginUrl);
    }
  }
}
